// Página AdminDataManagement - Gestión de Datos del Sistema
// Permite limpiar, exportar e importar datos de las colecciones

import { useState } from 'react'
import { clearCollection, exportData, importData, migrateLegacyProductData } from '../../services/dataManagement.service'
import Button from '../../components/Button'
import Toast from '../../components/Toast'
import './AdminDataManagement.css'

function AdminDataManagement() {
  const [loading, setLoading] = useState(false)
  const [notification, setNotification] = useState(null)
  const [confirmClear, setConfirmClear] = useState(false)
  const [migrating, setMigrating] = useState(false)

  // Colecciones para limpiar (según solicitud del usuario)
  const cleanOptions = [
    { id: 'orders', label: 'Pedidos (Orders)' },
    { id: 'sales', label: 'Ventas (Sales)' },
    { id: 'stock', label: 'Stock Actual' },
    { id: 'stockMovements', label: 'Movimientos de Stock' },
    { id: 'purchaseOrders', label: 'Órdenes de Compra' }
  ]

  const [selectedClean, setSelectedClean] = useState([])

  // Colecciones para exportar/importar
  const dataCollections = [
    'products',
    'suppliers',
    'warehouses',
    'stock',
    'stockMovements',
    'purchaseOrders',
    'sales',
    'orders',
    'users',
    'shippingSettings',
    'helpSettings',
    'siteSettings',
    'categories',
    'subcategories',
    'profitSettings',
    'alertsSettings',
    'supplies',
    'offers'
  ]

  const handleCleanSelection = (id) => {
    setSelectedClean(prev => 
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    )
  }

  const handleSelectAllClean = () => {
    if (selectedClean.length === cleanOptions.length) {
      setSelectedClean([])
    } else {
      setSelectedClean(cleanOptions.map(opt => opt.id))
    }
  }

  const handleClearData = async () => {
    if (selectedClean.length === 0) {
      setNotification({ type: 'error', message: 'Selecciona al menos una tabla para limpiar' })
      return
    }

    if (!confirmClear) {
      setConfirmClear(true)
      return
    }

    try {
      setLoading(true)
      let total = 0
      
      for (const collection of selectedClean) {
        const count = await clearCollection(collection)
        total += count
      }

      setNotification({ type: 'success', message: `Datos eliminados correctamente (${total} documentos)` })
      setSelectedClean([])
      setConfirmClear(false)
    } catch (error) {
      console.error('Error limpiando datos:', error)
      setNotification({ type: 'error', message: 'Error al limpiar los datos' })
    } finally {
      setLoading(false)
    }
  }

  const handleExport = async () => {
    try {
      setLoading(true)
      const data = await exportData(dataCollections)
      
      // Crear blob y descargar
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `pinohome_backup_${new Date().toISOString().split('T')[0]}.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)

      setNotification({ type: 'success', message: 'Exportación completada correctamente' })
    } catch (error) {
      console.error('Error exportando:', error)
      setNotification({ type: 'error', message: 'Error al exportar los datos' })
    } finally {
      setLoading(false)
    }
  }

  const handleImport = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    try {
      setLoading(true)
      const reader = new FileReader()
      
      reader.onload = async (event) => {
        try {
          const jsonData = JSON.parse(event.target.result)
          const results = await importData(jsonData, true) // true para limpiar antes de importar (opcional, pero recomendado para restore completo)
          
          const totalDocs = Object.values(results).reduce((a, b) => a + b, 0)
          setNotification({ type: 'success', message: `Importación completada (${totalDocs} documentos restaurados)` })
        } catch (err) {
          console.error('Error procesando archivo:', err)
          setNotification({ type: 'error', message: 'Error al procesar el archivo JSON' })
        } finally {
          setLoading(false)
        }
      }

      reader.readAsText(file)
    } catch (error) {
      console.error('Error importando:', error)
      setNotification({ type: 'error', message: 'Error al leer el archivo' })
      setLoading(false)
    }
  }

  const handleMigrateLegacyData = async () => {
    try {
      setMigrating(true)
      setNotification({ type: 'info', message: 'Iniciando migración de datos legacy...' })
      const migratedCount = await migrateLegacyProductData()
      setNotification({ type: 'success', message: `Migración completada: ${migratedCount} productos actualizados` })
    } catch (error) {
      console.error('Error al migrar datos legacy:', error)
      setNotification({ type: 'error', message: 'Error al migrar datos legacy' })
    } finally {
      setMigrating(false)
    }
  }

  return (
    <div className="admin-data-management">
      <Toast
        isVisible={!!notification}
        message={notification?.message || ''}
        type={notification?.type || 'error'}
        onClose={() => setNotification(null)}
      />

      <div className="data-header">
        <h1>Manejo de Datos</h1>
        <p>Herramientas para limpieza y respaldo del sistema</p>
      </div>

      <div className="data-sections">
        {/* Sección de Limpieza */}
        <section className="data-section danger-zone">
          <h2>⚠️ Limpieza de Datos</h2>
          <p className="section-desc">Selecciona las tablas que deseas vaciar. Esta acción es irreversible.</p>
          
          <div className="clean-options">
            <div className="option-item select-all">
              <label>
                <input 
                  type="checkbox" 
                  checked={selectedClean.length === cleanOptions.length}
                  onChange={handleSelectAllClean}
                  disabled={loading}
                />
                <strong>Seleccionar Todo</strong>
              </label>
            </div>
            
            {cleanOptions.map(option => (
              <div key={option.id} className="option-item">
                <label>
                  <input 
                    type="checkbox" 
                    checked={selectedClean.includes(option.id)}
                    onChange={() => handleCleanSelection(option.id)}
                    disabled={loading}
                  />
                  {option.label}
                </label>
              </div>
            ))}
          </div>

          <div className="action-buttons">
            {!confirmClear ? (
              <Button 
                variant="danger" 
                onClick={handleClearData}
                disabled={loading || selectedClean.length === 0}
              >
                🗑️ Limpiar Tablas Seleccionadas
              </Button>
            ) : (
              <div className="confirm-actions">
                <p className="confirm-text">¿Estás seguro? Se borrarán todos los datos seleccionados.</p>
                <Button 
                  variant="danger" 
                  onClick={handleClearData}
                  disabled={loading}
                >
                  Sí, borrar definitivamente
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setConfirmClear(false)}
                  disabled={loading}
                >
                  Cancelar
                </Button>
              </div>
            )}
          </div>
        </section>

        {/* Sección de Respaldo */}
        <section className="data-section backup-zone">
          <h2>💾 Copia de Seguridad</h2>
          <p className="section-desc">Exporta o importa toda la base de datos en formato JSON.</p>
          
          <div className="backup-actions">
            <div className="backup-card">
              <h3>Exportar Datos</h3>
              <p>Descarga un archivo JSON con todos los datos del sistema.</p>
              <Button 
                variant="primary" 
                onClick={handleExport}
                disabled={loading}
              >
                ⬇️ Exportar Todo
              </Button>
            </div>

            <div className="backup-card">
              <h3>Importar Datos</h3>
              <p>Restaura datos desde un archivo JSON (Sobreescribirá datos existentes).</p>
              <div className="file-input-wrapper">
                <input 
                  type="file" 
                  accept=".json" 
                  onChange={handleImport}
                  disabled={loading}
                  id="import-file"
                />
                <label htmlFor="import-file" className={`btn btn-outline ${loading ? 'disabled' : ''}`}>
                  ⬆️ Seleccionar Archivo
                </label>
              </div>
            </div>
          </div>
        </section>

        <section className="data-section migration-zone">
          <h2>🔄 Migración de Datos Antiguos</h2>
          <p className="section-desc">Convierte imágenes, SKU y Nro de Serie del producto a variantes.</p>
          <Button 
            variant="primary" 
            onClick={handleMigrateLegacyData}
            disabled={loading || migrating}
          >
            {migrating ? 'Migrando...' : 'Migrar Datos Legacy'}
          </Button>
        </section>
      </div>
    </div>
  )
}

export default AdminDataManagement
